import { defineComponent, computed, ref, watch, Fragment } from 'vue';
import PropTypes from '../_util/vue-types';
import { filterEmpty } from '../_util/props-util';
import { booleanType, objectType, someType, tuple } from '../_util/type';
import useConfigInject from '../config-provider/hooks/useConfigInject';
import useFlexGapSupport from '../_util/hooks/useFlexGapSupport';
import classNames from '../_util/classNames';
import Compact from './Compact';
import Addon from './Addon';
import useStyle from './style';
const spaceSize = {
    small: 8,
    middle: 16,
    large: 24,
};
export const spaceProps = () => ({
    prefixCls: String,
    size: {
        type: [String, Number, Array],
    },
    direction: PropTypes.oneOf(tuple('horizontal', 'vertical')).def('horizontal'),
    /** The space direction (preferred alias of direction) */
    orientation: PropTypes.oneOf(tuple('horizontal', 'vertical')),
    /** Syntactic sugar for `orientation="vertical"` */
    vertical: booleanType(),
    align: PropTypes.oneOf(tuple('start', 'end', 'center', 'baseline')),
    wrap: booleanType(),
    split: someType([Object, String, Number, Boolean]),
    /** Set separator between items (preferred alias of split) */
    separator: someType([Object, String, Number, Boolean]),
    /** Semantic structure className (antd ≥ 5.x). */
    classNames: objectType(),
    /** Semantic structure style (antd ≥ 5.x). */
    styles: objectType(),
});
function getNumberSize(size) {
    return typeof size === 'string' ? spaceSize[size] : size || 0;
}
const Space = defineComponent({
    compatConfig: { MODE: 3 },
    name: 'ASpace',
    inheritAttrs: false,
    props: spaceProps(),
    slots: Object,
    setup(props, { slots, attrs }) {
        const { prefixCls, space, direction: directionConfig } = useConfigInject('space', props);
        const [wrapSSR, hashId] = useStyle(prefixCls);
        const supportFlexGap = useFlexGapSupport();
        const size = computed(() => { var _a, _b, _c; return (_c = (_a = props.size) !== null && _a !== void 0 ? _a : (_b = space === null || space === void 0 ? void 0 : space.value) === null || _b === void 0 ? void 0 : _b.size) !== null && _c !== void 0 ? _c : 'small'; });
        const horizontalSize = ref();
        const verticalSize = ref();
        watch(size, () => {
            [horizontalSize.value, verticalSize.value] = (Array.isArray(size.value) ? size.value : [size.value, size.value]).map(item => getNumberSize(item));
        }, { immediate: true });
        const mergedOrientation = computed(() => {
            if (props.orientation === 'vertical' || props.orientation === 'horizontal') {
                return props.orientation;
            }
            if (props.vertical) {
                return 'vertical';
            }
            return props.direction === 'vertical' ? 'vertical' : 'horizontal';
        });
        const mergedAlign = computed(() => props.align === undefined && mergedOrientation.value === 'horizontal'
            ? 'center'
            : props.align);
        const cn = computed(() => {
            var _a, _b;
            return classNames(prefixCls.value, hashId.value, `${prefixCls.value}-${mergedOrientation.value}`, {
                [`${prefixCls.value}-rtl`]: directionConfig.value === 'rtl',
                [`${prefixCls.value}-align-${mergedAlign.value}`]: mergedAlign.value,
            }, (_a = space === null || space === void 0 ? void 0 : space.value) === null || _a === void 0 ? void 0 : _a.className, (_b = props.classNames) === null || _b === void 0 ? void 0 : _b.root);
        });
        const marginDirection = computed(() => directionConfig.value === 'rtl' ? 'marginLeft' : 'marginRight');
        const style = computed(() => {
            var _a, _b;
            const gapStyle = {};
            if (supportFlexGap.value) {
                gapStyle.columnGap = `${horizontalSize.value}px`;
                gapStyle.rowGap = `${verticalSize.value}px`;
            }
            return Object.assign(Object.assign(Object.assign(Object.assign({}, gapStyle), (props.wrap && { flexWrap: 'wrap', marginBottom: `${-verticalSize.value}px` })), (_a = space === null || space === void 0 ? void 0 : space.value) === null || _a === void 0 ? void 0 : _a.style), (_b = props.styles) === null || _b === void 0 ? void 0 : _b.root);
        });
        return () => {
            var _a, _b, _c, _d, _e, _f;
            const { wrap, classNames: semanticClassNames, styles: semanticStyles } = props;
            const direction = mergedOrientation.value;
            const children = (_a = slots.default) === null || _a === void 0 ? void 0 : _a.call(slots);
            const items = filterEmpty(children);
            const len = items.length;
            if (len === 0) {
                return null;
            }
            const split = (_e = (_c = (_b = props.separator) !== null && _b !== void 0 ? _b : props.split) !== null && _c !== void 0 ? _c : (_d = slots.separator) === null || _d === void 0 ? void 0 : _d.call(slots)) !== null && _e !== void 0 ? _e : (_f = slots.split) === null || _f === void 0 ? void 0 : _f.call(slots);
            const itemClassName = `${prefixCls.value}-item`;
            const horizontalSizeVal = horizontalSize.value;
            const latestIndex = len - 1;
            return (<div {...attrs} class={[cn.value, attrs.class]} style={[style.value, attrs.style]}>
          {items.map((child, index) => {
                    let originIndex = children.indexOf(child);
                    if (originIndex === -1) {
                        originIndex = `$$space-${index}`;
                    }
                    let itemStyle = {};
                    if (!supportFlexGap.value) {
                        if (direction === 'vertical') {
                            if (index < latestIndex) {
                                itemStyle = { marginBottom: `${horizontalSizeVal / (split ? 2 : 1)}px` };
                            }
                        }
                        else {
                            itemStyle = Object.assign(Object.assign({}, (index < latestIndex && {
                                [marginDirection.value]: `${horizontalSizeVal / (split ? 2 : 1)}px`,
                            })), (wrap && { paddingBottom: `${verticalSize.value}px` }));
                        }
                    }
                    return wrapSSR(<Fragment key={originIndex}>
                <div class={classNames(itemClassName, semanticClassNames === null || semanticClassNames === void 0 ? void 0 : semanticClassNames.item)} style={Object.assign(Object.assign({}, itemStyle), semanticStyles === null || semanticStyles === void 0 ? void 0 : semanticStyles.item)}>
                  {child}
                </div>
                {index < latestIndex && split && (<span class={classNames(`${itemClassName}-split`, semanticClassNames === null || semanticClassNames === void 0 ? void 0 : semanticClassNames.separator)} style={Object.assign(Object.assign({}, itemStyle), semanticStyles === null || semanticStyles === void 0 ? void 0 : semanticStyles.separator)}>
                    {split}
                  </span>)}
              </Fragment>);
                })}
        </div>);
        };
    },
});
Space.Compact = Compact;
Space.Addon = Addon;
Space.install = function (app) {
    app.component(Space.name, Space);
    app.component(Compact.name, Compact);
    app.component(Addon.name, Addon);
    return app;
};
export { Compact, Addon };
export default Space;
